import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { supabase } from "./supabaseClient";

/**
 * Register for push notifications on native platforms.
 * Requests permission, gets FCM/APNs token, and saves it to DB.
 * No-op on web.
 */
export async function registerPushNotifications(userEmail) {
  if (!Capacitor.isNativePlatform() || !userEmail) return;

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== "granted") return;

  PushNotifications.addListener("registration", async (token) => {
    const platform = Capacitor.getPlatform(); // 'ios' or 'android'
    await savePushToken(userEmail, token.value, platform);
  });

  PushNotifications.addListener("registrationError", (error) => {
    console.error("Push registration error:", error);
  });

  try {
    await PushNotifications.register();
  } catch (e) {
    console.error("Push register failed:", e);
  }
}

/**
 * Save or upsert a push token to the push_tokens table.
 */
async function savePushToken(userEmail, token, platform) {
  const { error } = await supabase.from("push_tokens").upsert(
    {
      user_email: userEmail,
      token,
      platform,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_email,token" }
  );
  if (error) {
    console.error("Failed to save push token:", error);
  }
}

/**
 * Remove the current device's push token (e.g. on logout).
 */
export async function unregisterPushNotifications(userEmail) {
  if (!Capacitor.isNativePlatform() || !userEmail) return;

  try {
    await PushNotifications.removeAllListeners();
  } catch (e) {
    // ignore
  }
}

/**
 * Set up listeners for incoming push notifications.
 * - foreground: show in-app (already handled by NotificationContext polling)
 * - tap: navigate to the notification's link
 */
export function setupPushListeners(navigateFn, refreshCountFn) {
  if (!Capacitor.isNativePlatform()) return;

  // Notification received while app is in foreground
  PushNotifications.addListener("pushNotificationReceived", (notification) => {
    // Refresh the in-app unread count
    if (refreshCountFn) refreshCountFn();
  });

  // Notification tapped (from background/killed)
  PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    const data = action.notification.data;
    if (data?.link && navigateFn) {
      navigateFn(data.link);
    }
    if (refreshCountFn) refreshCountFn();
  });
}
